import { useEffect, useRef, useState } from "react";
import { Camera, RotateCcw, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { compressForUpload, type CompressedPhoto } from "@/lib/storage/proofPhotos";

type Props = {
  /**
   * Wywołane gdy użytkownik wybrał i skompresował zdjęcie. Zewnętrzny
   * komponent decyduje co zrobić (np. „Submit" guzik wysyła upload).
   */
  onPhotoReady: (photo: CompressedPhoto) => void;
  onCleared: () => void;
  /** Optional label nad placeholderem. */
  label?: string;
  disabled?: boolean;
};

/**
 * PhotoCapture — picker zdjęcia dla flow completion.
 *
 * Zachowanie iOS PWA: `<input type=file capture=environment>` otwiera
 * natywny picker („Aparat / Biblioteka / Wybierz plik") — nie próbujemy
 * `getUserMedia()`, bo iOS PWA z home-screen miało z tym historię problemów.
 */
export function PhotoCapture({ onPhotoReady, onCleared, label, disabled }: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Cleanup blob URL przy unmount lub przy zmianie zdjęcia.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const open = () => inputRef.current?.click();

  const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset, żeby drugi raz ten sam plik wywołał event
    if (!file) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const photo = await compressForUpload(file);
      const url = URL.createObjectURL(photo.blob);
      // Zwolnij stare preview
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
      const kb = Math.round(photo.sizeBytes / 1024);
      setInfo(`Skompresowane do ${kb} KB · ${photo.width}×${photo.height}`);
      onPhotoReady(photo);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Błąd kompresji");
    } finally {
      setBusy(false);
    }
  };

  const clear = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setInfo(null);
    setError(null);
    onCleared();
  };

  return (
    <div className="space-y-2">
      {label ? (
        <p className="text-xs text-muted">{label}</p>
      ) : null}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onChange}
      />

      {previewUrl ? (
        <div className="space-y-2">
          <div className="relative overflow-hidden rounded-[14px] border border-line bg-soft">
            <img
              src={previewUrl}
              alt="Podgląd dowodu"
              className="w-full h-auto block"
            />
          </div>
          {info ? (
            <p className="text-[11px] text-muted">{info}</p>
          ) : null}
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={open}
              disabled={busy || disabled}
            >
              <RotateCcw className="h-4 w-4" />
              Zmień
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clear}
              disabled={busy || disabled}
            >
              Usuń
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={open}
          disabled={busy || disabled}
          className="w-full flex flex-col items-center justify-center gap-2 rounded-[14px] border-2 border-dashed border-line bg-soft px-4 py-8 text-muted hover:text-ink hover:border-ink/30 transition-colors disabled:opacity-50 disabled:pointer-events-none"
        >
          <Camera className="h-8 w-8" />
          <span className="text-sm font-medium">
            {busy ? "Przygotowuję…" : "Zrób zdjęcie / wybierz"}
          </span>
          <span className="text-[11px]">
            kompresja do ~150 KB w przeglądarce
          </span>
        </button>
      )}

      {error ? (
        <p className="flex items-center gap-1 text-xs text-rose-600">
          <AlertCircle className="h-3 w-3" />
          {error}
        </p>
      ) : null}
    </div>
  );
}
